import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubImageUpload from './ClubImageUpload';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

function DialogAppImage({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        defaultValues: {
            backgroundApp: '',
        },
    });

    useEffect(() => {
        if (!open || !data) return;

        form.reset({
            backgroundApp: data.backgroundApp || '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            if (data?.id) {
                await server.put('club', { backgroundApp: values.backgroundApp });
                enqueueSnackbar('Imagem da tela de login salva com sucesso.');
            }

            await mutate();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;
    const backgroundPreview = form.watch('backgroundApp');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-md">
                <ScrollDialogHeader>
                    <DialogTitle>Imagem da tela de login</DialogTitle>
                    <DialogDescription>
                        Aparece no app e no web. Dimensões recomendadas: 1080×1920px. Formato PNG ou JPEG.
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        {data?.id ? (
                            <FieldGroup>
                                <Field>
                                    <FieldLabel>Imagem da tela de login</FieldLabel>
                                    <ClubImageUpload
                                        name="backgroundApp"
                                        onChange={(_, value) =>
                                            form.setValue('backgroundApp', value ?? '', { shouldDirty: true })
                                        }
                                        value={backgroundPreview}
                                        canvaWidth={180}
                                        canvaHeight={320}
                                        objectFit="cover"
                                        label="Dimensões recomendadas: 1080x1920px (9:16). Formato PNG ou JPEG."
                                    />
                                </Field>
                            </FieldGroup>
                        ) : null}
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function AppImageClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#imagem-app');

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={ImageIcon}
                        title="Imagem da tela de login"
                        description="Usada na tela de login do app e do web."
                    />
                </CardHeader>
                <CardContent className="flex justify-center text-sm">
                    {data?.backgroundApp ? (
                        <img
                            src={data.backgroundApp}
                            alt="Imagem da tela de login"
                            className="aspect-[9/16] h-100 rounded-xl object-cover"
                        />
                    ) : (
                        <div className="flex aspect-[9/16] h-20 items-center justify-center rounded-xl border-2 border-dashed border-muted-foreground/40 px-2 text-center text-xs text-muted-foreground">
                            Nenhuma imagem
                        </div>
                    )}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#imagem-app" />}>
                        <ImageIcon className="size-4" />
                        {data?.backgroundApp ? 'Alterar' : 'Adicionar'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogAppImage data={data} mutate={mutate} open={open} onOpenChange={isOpen => !isOpen && handleClose()} />
        </>
    );
}
